import React from "react";

import { RateDto } from "arps_wasm";
import { ChartTypeLabels } from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { ArpFields, ArpsDesignerKind, ProductNames } from "constants/forecasts.constants";
import dayjs from "dayjs";

import {
  ArpSegment,
  ForecastConstant,
  UpdateProductTemplatesParams,
  UserArpsItem
} from "../../../models/UserArpsModel";
import { ICheckedForecast } from "../../../store/features";
import { isDecliningRatio, isPrimaryProduct, isRatio } from "../../../utils";
import { EPSILON, roundTo } from "../../../utils/numbers";
import { CumulativeData } from "../../forecasting/Forecasting";
import { UpdateEURParams } from "../hooks/useUpdateEUR";
import {
  ProductData,
  ProductSegmentTemplate,
  SegmentTemplate
} from "../models/SegmentTemplate";
import { TypeWellProductItem } from "../widget/TypeWellEditor/tabs/TypeWellProductOptions";
import {
  getTypeWellTemplateDisplayFieldsAndData,
  isConstrainedSegment,
  isRampUpSegment,
  isSAGDTemplate
} from "./arpsUtils";
import { getNewCalculatedFields } from "./calculatedFieldsUtils";
import {
  avgMonthsDiff,
  getDurationInDays,
  getFirstDayOfNextMonthUTC,
  getFractionalMonths,
  isNextMonth,
  numberOfMonthsAfterDate
} from "./dates";
import { getDeclineTitleWithType } from "./declineHelpers";
import { getRatioDisplayValues, updateRatioDeclineSegments } from "./ratioDeclineUtils";
import { scaleDataBasedOnYAxisUnits } from "./scaleDataBasedOnYAxisUnits";
import { updateSegmentsFromHeaderValues, updateSessionDf } from "./updateSegment";
import { arpsWasm } from "./wasmLoader";

const TYPE_WELL_MAX_NUMBER_OF_YEARS_CAP = 100;

export const IMPORTED_TEMPLATE_NAME = "Imported";

// Distinguishes a read-only rate column from an imported ratio column of the same name.
export const isImportedRateTemplate = (
  product: string,
  template?: SegmentTemplate
): boolean => !isRatio(product) && template?.name === IMPORTED_TEMPLATE_NAME;

export function calculateTimeForTimeSeriesCharts(
  yAxisTitle: string,
  rateCum: RateDto[],
  chartType: "Rate Time" | "Cum Time",
  timeStep: TimeStep
): [number, number, number | undefined][] {
  const scale = scaleDataBasedOnYAxisUnits(yAxisTitle);

  if (timeStep === TimeStep.Day) {
    // For daily time step, calculate cumulative days from actual dates
    let cumulativeDays = 0;
    let lastDate: Date | null = null;

    const series = rateCum.map((rc) => {
      const currentDate = new Date(Date.UTC(rc.year, rc.month - 1, rc.day));

      if (lastDate) {
        // Add the number of days since the last data point
        const daysDiff =
          (currentDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24);
        cumulativeDays += daysDiff;
      }

      lastDate = currentDate;

      return [
        cumulativeDays,
        chartType === EvaChart.RateTime ? rc.rate : rc.cum * scale,
        undefined
      ] as [number, number, number | undefined];
    });

    return series;
  } else {
    // Original monthly logic
    const { series } = rateCum.reduce(
      (acc, rc) => {
        const currentDate = new Date(Date.UTC(rc.year, rc.month - 1, rc.day));
        const incrementMonth = isNextMonth(acc.lastDate, currentDate);
        if (incrementMonth || acc.lastDate == null) {
          acc.monthIdx++;
        }
        acc.lastDate = currentDate;
        const nextTimeValue = getFractionalMonths(dayjs.utc(currentDate), acc.monthIdx);

        acc.series.push([
          nextTimeValue,
          chartType === EvaChart.RateTime ? rc.rate : rc.cum * scale,
          undefined
        ]);

        return acc;
      },
      { series: [], lastDate: null, monthIdx: -1 } as {
        series: [number, number, number | undefined][];
        lastDate: Date | null;
        monthIdx: number;
      }
    );

    return series;
  }
}

export function getTypeWellSeries(
  chartType: ChartTypeLabels,
  rateCum: RateDto[],
  yAxisTitle: string,
  timeStep: TimeStep
): [number | string, number, number | undefined][] {
  if (chartType === EvaChart.RateCum) {
    return rateCum.map((rc) => {
      return [rc.cum * 0.001, rc.rate, undefined];
    });
  }

  if (chartType === EvaChart.RateTime || chartType === EvaChart.CumTime) {
    return calculateTimeForTimeSeriesCharts(yAxisTitle, rateCum, chartType, timeStep);
  }

  if (chartType === EvaChart.RateDate) {
    const scale = scaleDataBasedOnYAxisUnits(yAxisTitle);
    const series = [];

    const date = new Date();
    const maxYear = date.getFullYear() + TYPE_WELL_MAX_NUMBER_OF_YEARS_CAP;

    rateCum
      .filter(
        (rc) =>
          rc.year < maxYear || (rc.year === maxYear && rc.month === 1 && rc.day === 1)
      )
      .forEach((rc) => {
        series.push([
          rc.year +
            "-" +
            rc.month.toString().padStart(2, "0") +
            "-" +
            rc.day.toString().padStart(2, "0") +
            "T00:00:00",
          rc.rate * scale,
          undefined
        ]);
      });

    return series;
  }
}

// What each ratio constant needs (inputs) and derives (outputs). A ratio can
// only be computed from a literal curve, never from another ratio's derived
// product, so the menu never offers a ratio whose input is merely derivable.
// Inputs mirror the engine exactly: SOR is the only ratio whose engine path
// reads condensate, so GOR/WOR/1+WOR require a literal oil curve (offering
// them on condensate yielded empty derived curves; a condensate fallback can
// be added engine-side if users ask for it).
//
// Gating, applied per ratio in the loop below:
// - Not selected: offered only when one of its input curves exists, none of
//   its outputs is already defined (as a curve or derivably), and no
//   conflicting ratio is selected.
// - Selected: its outputs leave the menu (they are now computed). When an
//   output is already defined anyway, or a conflicting ratio also derives it
//   (imported/legacy states the menu can no longer create), the inputs are
//   blocked too so the output cannot be defined twice.
interface RatioRule {
  inputs: ProductNames[];
  outputs: ProductNames[];
}

const RATIO_CONSTANT_RULES = {
  [ProductNames.GOR]: {
    inputs: [ProductNames.Oil],
    outputs: [ProductNames.Gas]
  },
  [ProductNames.CGR]: {
    inputs: [ProductNames.Gas],
    outputs: [ProductNames.Oil, ProductNames.Condensate]
  },
  [ProductNames.OGR]: {
    inputs: [ProductNames.Gas],
    outputs: [ProductNames.Oil, ProductNames.Condensate]
  },
  [ProductNames.WGR]: {
    inputs: [ProductNames.Gas],
    outputs: [ProductNames.Water]
  },
  [ProductNames.WOR]: {
    inputs: [ProductNames.Oil],
    outputs: [ProductNames.Water]
  },
  [ProductNames.OnePlusWOR]: {
    inputs: [ProductNames.Oil],
    outputs: [ProductNames.Water]
  },
  [ProductNames.SOR]: {
    inputs: [ProductNames.Oil, ProductNames.Condensate],
    outputs: [ProductNames.Steam]
  }
} satisfies Record<string, RatioRule>;

type RatioProduct = keyof typeof RATIO_CONSTANT_RULES;

// Widened view for lookups: indexing the `satisfies`-inferred table by the key
// union would intersect the per-rule array types and break `.includes`.
const RATIO_RULES: Record<RatioProduct, RatioRule> = RATIO_CONSTANT_RULES;

const RATIO_RULE_ENTRIES = Object.entries(RATIO_RULES) as [RatioProduct, RatioRule][];

// Ratios whose outputs overlap conflict with each other — only one of them may
// define a product at a time. Derived from the table so the two can't drift.
const conflictsOf = (ratio: RatioProduct): RatioProduct[] =>
  RATIO_RULE_ENTRIES.filter(
    ([other, rule]) =>
      other !== ratio &&
      rule.outputs.some((output) => RATIO_RULES[ratio].outputs.includes(output))
  ).map(([other]) => other);

const CONDENSATE_SPELLINGS = new Set<string>([
  ProductNames.CondAbbreviated,
  ProductNames.CondAbbreviatedWithDot
]);

const collectSelectedProducts = (
  segments: Maybe<ArpSegment[]>,
  constants: Maybe<ForecastConstant[]>
): Set<string> => {
  const selected = new Set<string>();
  for (const product of [
    ...(segments?.map((item) => item.product) ?? []),
    ...(constants?.map((item) => item.product) ?? [])
  ]) {
    if (!product) continue;
    const upper = String(product).toUpperCase();
    selected.add(upper);
    // Abbreviated condensate spellings must gate exactly like the full name.
    if (CONDENSATE_SPELLINGS.has(upper)) {
      selected.add(ProductNames.Condensate);
    }
  }
  return selected;
};

// The C2–C5 yield constants multiply a SALES GAS forecast (arps-wasm
// get_liquids_forecast): sales gas is derivable from a Sales Gas curve, or
// Shrinkage with a gas source — a Gas curve, or Oil + GOR (the one deliberate
// double-hop the engine supports). Known accepted limitation, shared with the
// shrinkage-pair rule: the GOR check cannot distinguish a GOR constant from a
// declining GOR segment (the selected set merges both sources), while the
// engine's sales-gas gate reads GOR constants only — an Oil + declining-GOR +
// Shrinkage type well is offered C2–C5 the engine cannot compute.
// Deliberately NARROWER than the engine in one legacy-only direction too: the
// engine also derives gas from a GOR constant paired with 1+WOR segments; the
// menu never creates that pairing, so imported forecasts in that state lose
// C2–C5 (and Shrinkage) here — accepted, matching the menu's own rules.
const salesGasDerivableFrom = (selected: Set<string>): boolean =>
  selected.has(ProductNames.SalesGas) ||
  (selected.has(ProductNames.Shrinkage) &&
    (selected.has(ProductNames.Gas) ||
      (selected.has(ProductNames.GOR) && selected.has(ProductNames.Oil))));

// O+W needs one of its components as a literal curve — the engine's FLAT O+W
// path derives water from literal Oil segments (condensate does not count
// there; the DECLINING O+W path can fall back to condensate via
// get_oil_forecast, an intentionally unreached case under the agreed Oil-only
// narrowing), and product wants water-first supported.
const OPW_COMPONENTS = [ProductNames.Oil, ProductNames.Water];
const hasOilPlusWaterComponent = (selected: Set<string>): boolean =>
  OPW_COMPONENTS.some((p) => selected.has(p));

// Constants whose engine inputs can disappear when a product row is deleted.
// The menu gates their ENTRY; this prunes them on the EXIT path so a type
// well can't be left holding a constant that silently produces empty curves
// (delete Shrinkage and an added C2 would otherwise survive it). Classic
// ratios are deliberately NOT pruned — dangling ratios are a modeled legacy
// state with a repair path (re-add the input), per the EVA-121 design.
export function pruneOrphanedDependentConstants(
  segments: ArpSegment[],
  constants: ForecastConstant[]
): { kept: ForecastConstant[]; pruned: ForecastConstant[] } {
  const selected = collectSelectedProducts(segments, constants);
  const salesGasOk = salesGasDerivableFrom(selected);
  const opwOk = hasOilPlusWaterComponent(selected);
  const yieldConstants = new Set<string>([
    ProductNames.C2,
    ProductNames.C3,
    ProductNames.C4,
    ProductNames.C5
  ]);

  const kept: ForecastConstant[] = [];
  const pruned: ForecastConstant[] = [];
  for (const constant of constants) {
    const product = String(constant.product).toUpperCase();
    const orphaned =
      (yieldConstants.has(product) && !salesGasOk) ||
      (product === ProductNames.OilPlusWater && !opwOk);
    (orphaned ? pruned : kept).push(constant);
  }
  return { kept, pruned };
}

export function getAvailableProducts(
  arps: ICheckedForecast,
  typeWellProductsMenuItems: TypeWellProductItem[]
) {
  const selected = collectSelectedProducts(arps?.arps, arps?.constants);

  const isSelected = (p: string) => selected.has(p);
  const anySelected = (ps: string[]) => ps.some(isSelected);

  // The shrinkage pair makes gas derivable from a Sales Gas curve; a derived
  // product counts as "defined" when checking ratio outputs below (e.g. GOR
  // must not be offered — nor keep its inputs — once gas is already
  // derivable). Only the Sales Gas → Gas direction matters here: Gas is the
  // only member of the pair that appears in a ratio's outputs.
  const defined = new Set<string>(selected);
  if (isSelected(ProductNames.Shrinkage) && isSelected(ProductNames.SalesGas)) {
    defined.add(ProductNames.Gas);
  }
  const anyDefined = (ps: string[]) => ps.some((p) => defined.has(p));

  // Selected products never reappear in the menu.
  const remove = new Set<string>(selected);

  // Oil and condensate are mutually exclusive curves.
  if (isSelected(ProductNames.Condensate)) remove.add(ProductNames.Oil);
  if (isSelected(ProductNames.Oil)) remove.add(ProductNames.Condensate);

  // Inputs are checked with `anySelected` (literal curves only — the engine
  // cannot chain a ratio off a derived product); outputs with `anyDefined`.
  for (const [ratio, rule] of RATIO_RULE_ENTRIES) {
    const conflicts = conflictsOf(ratio);
    if (selected.has(ratio)) {
      // The ratio's output is now computed — it can't also be added as a curve.
      rule.outputs.forEach((p) => remove.add(p));
      if (anyDefined(rule.outputs) || conflicts.some((c) => selected.has(c))) {
        rule.inputs.forEach((p) => remove.add(p));
      }
    } else if (
      !anySelected(rule.inputs) ||
      anyDefined(rule.outputs) ||
      conflicts.some((c) => selected.has(c))
    ) {
      remove.add(ratio);
    }
  }

  // Shrinkage is a bidirectional pair: Sales Gas = Gas × Shrinkage. Gas also
  // counts as defined via Oil + GOR — the one derived input the engine
  // deliberately supports for shrinkage. Oil only: the engine's shrinkage
  // path does not read condensate curves.
  const gasDefined =
    isSelected(ProductNames.Gas) ||
    (isSelected(ProductNames.GOR) && isSelected(ProductNames.Oil));
  const salesGasDefined = isSelected(ProductNames.SalesGas);
  if (isSelected(ProductNames.Shrinkage)) {
    // Whichever side of the pair is missing is now derivable — block adding it.
    if (gasDefined && !salesGasDefined) remove.add(ProductNames.SalesGas);
    if (salesGasDefined && !isSelected(ProductNames.Gas)) {
      remove.add(ProductNames.Gas);
    }
  } else if (gasDefined === salesGasDefined) {
    // Offered only when exactly one of the pair is defined.
    remove.add(ProductNames.Shrinkage);
  }

  // The C2–C5 yield constants and O+W sit outside the ratio rules table.
  //
  // C2–C5 require derivable sales gas (see salesGasDerivableFrom): with no
  // derivable sales gas the constants produce empty curves, silently. Note a
  // raw Gas curve ALONE is not enough: without Shrinkage there is no sales
  // gas to multiply.
  if (!salesGasDerivableFrom(selected)) {
    [ProductNames.C2, ProductNames.C3, ProductNames.C4, ProductNames.C5].forEach((p) =>
      remove.add(p)
    );
  }

  // O+W needs one of its components (see hasOilPlusWaterComponent). It is
  // deliberately not modeled as a water-deriving ratio in the rules table
  // (output-gating would forbid the water-first case product requires). Known
  // accepted gap from that carve-out: alongside a dangling water ratio, O+W
  // can define water a second time (the engine resolves by precedence).
  if (!hasOilPlusWaterComponent(selected)) {
    remove.add(ProductNames.OilPlusWater);
  }
  // With Water already a curve, O+W has nothing left to define: alongside Oil
  // both components exist, and alongside Condensate there is no literal Oil
  // curve for the engine's O+W path to read (condensate is not O+W's oil
  // component), so O+W could only redefine water.
  if (
    isSelected(ProductNames.Water) &&
    (isSelected(ProductNames.Oil) || isSelected(ProductNames.Condensate))
  ) {
    remove.add(ProductNames.OilPlusWater);
  }

  return typeWellProductsMenuItems.filter((item) => !remove.has(item.key ?? ""));
}

function getArpsSegmentTemplate({
  segments,
  typeWellTemplates
}: {
  segments: ArpSegment[];
  typeWellTemplates: SegmentTemplate[];
}): SegmentTemplate | undefined {
  const segmentLength = segments.length;

  if (segmentLength === 1) {
    return typeWellTemplates.find((template) => template.name.includes("One Seg."));
  }

  const hasRampUpSegment = isRampUpSegment({ segmentTemplateName: null, segments });
  const hasConstrainedSegment = isConstrainedSegment({
    segments,
    segmentTemplateName: null
  });
  const isSAGD = isSAGDTemplate({ segmentTemplateName: null, segments });

  if (segmentLength === 2) {
    // Check for SAGD (2-segment mode: flat + decline)
    if (isSAGD) {
      const sagdTemplate = typeWellTemplates.find((template) => template.name === "SAGD");
      return sagdTemplate;
    }
    return typeWellTemplates.find((template) =>
      template.name.includes("Two Seg. Hyperbolic")
    );
  }

  if (segmentLength === 3) {
    // Check for SAGD with ramp-up (3-segment mode: ramp + flat + decline)
    if (isSAGD) {
      const sagdTemplate = typeWellTemplates.find((template) => template.name === "SAGD");
      return sagdTemplate;
    }
    if (hasRampUpSegment) {
      return typeWellTemplates.find((template) =>
        template.name.includes("Two Seg. Hyperbolic with Ramp-Up")
      );
    }
    return typeWellTemplates.find((template) =>
      template.name.includes("Three Seg. Transient")
    );
  }

  if (segmentLength === 4) {
    if (hasConstrainedSegment) {
      return typeWellTemplates.find((template) =>
        template.name.includes("Three Seg. Transient with Constrained Period")
      );
    }
    if (hasRampUpSegment) {
      return typeWellTemplates.find((template) =>
        template.name.includes("Three Seg. Transient with Ramp-Up")
      );
    }
  }

  return undefined;
}

export const validateSegments = ({
  arpsSegments,
  productSegmentTemplate,
  kind,
  cumulativeData,
  primaryProductEndDate,
  isPrimaryTypeWellSegment
}: {
  arpsSegments: ArpSegment[];
  productSegmentTemplate: ProductSegmentTemplate;
  kind: ArpsDesignerKind;
  cumulativeData: CumulativeData;
  primaryProductEndDate: Date;
  isPrimaryTypeWellSegment: boolean;
}) => {
  const errors = [];
  const warnings = [];

  const product = arpsSegments?.[0]?.product ?? null;

  if (!product) {
    return {
      errors,
      warnings
    };
  }

  const primaryProduct: string = getPrimaryProduct({
    kind,
    products: Object.keys(productSegmentTemplate),
    cumulativeData
  }).toLowerCase();

  const segmentTemplate = productSegmentTemplate[product];
  const segmentTemplateName = segmentTemplate?.name;

  const hasRampUpSegment = isRampUpSegment({
    segments: arpsSegments,
    segmentTemplateName
  });
  const foundErrors = arpsWasm.checkSegmentsForError(arpsSegments, hasRampUpSegment);

  // First pass: identify segments with Qi ≈ Qf or Qi < Qf due to constraint logic
  const constrainedSegmentKeys = new Set<string>();
  arpsSegments.forEach((segment, index) => {
    const segmentNumber = index + 1;

    // Skip ramp-up segments - they intentionally have qi < qf (rate increasing)
    // Ramp-up is always index 0 when hasRampUpSegment is true
    const isRampUpSeg = hasRampUpSegment && index === 0;
    if (isRampUpSeg) {
      return;
    }

    // Skip flat/constant segments - they intentionally have qi = qf with di = 0
    // This is different from a constrained segment which would have di > 0
    const isFlatSegment =
      Math.abs(segment.qi - segment.qf) < EPSILON && Math.abs(segment.di) < EPSILON;
    if (isFlatSegment) {
      return;
    }

    if (segment.qf) {
      const qiEqualsQf = Math.abs(segment.qi - segment.qf) < EPSILON;
      const qiLessThanQf = segment.qi < segment.qf;

      // Check if this segment's Qf matches a later segment's Qf (indicating constraint)
      // This handles when we constrain the qf of the overall forecast and don't allow it to decline past that value
      const hasLaterSegmentWithSameQf = arpsSegments
        .slice(index + 1)
        .some(
          (laterSeg) =>
            laterSeg.product === segment.product &&
            Math.abs(laterSeg.qf - segment.qf) < EPSILON
        );

      // Constrained segment if:
      // 1. Qi ≈ Qf (normal constraint case) OR
      // 2. Qi < Qf AND later segment has same Qf (over-constrained case)
      if ((qiEqualsQf && segment.di > 0) || (qiLessThanQf && hasLaterSegmentWithSameQf)) {
        constrainedSegmentKeys.add(`${segment.product}-${segmentNumber}`);
      }
    }
  });

  // Helper function to find which segment actually reached Qf
  const getSegmentThatReachedQf = (
    productSegments: ArpSegment[],
    constrainedSegmentIndex: number
  ): number => {
    // Walk backwards from the constrained segment to find the last valid segment
    for (let i = constrainedSegmentIndex - 1; i >= 0; i--) {
      const segmentNumber = i + 1;
      const key = `${productSegments[i].product}-${segmentNumber}`;

      if (!constrainedSegmentKeys.has(key)) {
        return segmentNumber;
      }
    }
    return 1;
  };

  // Add warnings for all constrained segments (segments where Qf already reached)
  arpsSegments.forEach((segment, index) => {
    const segmentNumber = index + 1;
    const key = `${segment.product}-${segmentNumber}`;

    if (constrainedSegmentKeys.has(key)) {
      const productSegments = arpsSegments.filter((s) => s.product === segment.product);
      const reachedInSegment = getSegmentThatReachedQf(productSegments, index);

      warnings.push({
        product: segment.product,
        segment: segmentNumber,
        message: `Segment ${segmentNumber}: Not required, Qf already reached in segment ${reachedInSegment}`
      });
    }
  });

  // Add errors for non-constrained segments that WASM flagged
  for (const err of foundErrors) {
    const key = `${err.product}-${err.segment}`;
    const isConstrainedSegment = constrainedSegmentKeys.has(key);

    // Only add as error if it's NOT a constrained segment
    // (constrained segments already handled above as warnings)
    if (!isConstrainedSegment) {
      const message = `Segment ${err.segment}: ${err.error}`;

      errors.push({
        product: err.product,
        segment: err.segment,
        message
      });
    }
  }

  arpsSegments.forEach((segment, index) => {
    const isLastSegment: boolean = index === arpsSegments.length - 1;
    const segmentNumber = arpsSegments.indexOf(segment) + 1;

    //check end dates
    try {
      const endDate = new Date(Date.parse(segment.endDate));
      const dateDiff = endDate.getUTCFullYear() - new Date().getUTCFullYear();
      if (dateDiff > 100) {
        warnings.push({
          product: segment.product,
          segment: segmentNumber,
          message: `Segment ${segmentNumber}: End date runs past 100 years`
        });
      }

      const isGreaterThanSixMonths =
        numberOfMonthsAfterDate(primaryProductEndDate, endDate) >= 6 && isLastSegment;

      const doesSecondaryEndBeforePrimaryProduct =
        primaryProductEndDate && endDate && !isPrimaryTypeWellSegment && isLastSegment
          ? endDate < primaryProductEndDate
          : false;

      const hasTypeWellDateWarning: boolean =
        kind === ArpsDesignerKind.TypeWell &&
        primaryProductEndDate &&
        !isPrimaryTypeWellSegment &&
        isGreaterThanSixMonths;

      const hasForecastDateWarning: boolean =
        kind === ArpsDesignerKind.Forecasting &&
        primaryProductEndDate &&
        segment.product !== cumulativeData?.primaryProduct &&
        isGreaterThanSixMonths;

      if (hasTypeWellDateWarning || hasForecastDateWarning) {
        warnings.push({
          product: segment.product,
          segment: segmentNumber,
          message: `Segment ${segmentNumber}: Primary product (${primaryProduct?.toUpperCase()}) end date is 6+ months earlier`
        });
      }

      if (doesSecondaryEndBeforePrimaryProduct) {
        warnings.push({
          product: segment.product,
          segment: segmentNumber,
          message: `Segment ${segmentNumber}: Secondary product ends before primary product (${primaryProduct?.toUpperCase()})`
        });
      }
    } catch (err) {
      warnings.push({
        product: segment.product,
        segment: segmentNumber,
        message: `Segment ${segmentNumber}: End date runs past 100 years`
      });
    }

    if (segment.b > 2) {
      warnings.push({
        product: segment.product,
        segment: segmentNumber,
        message: `Segment ${segmentNumber}: B values higher than 2 may cause an error`
      });
    }

    // check for forecast date < last production date
    if (
      kind === ArpsDesignerKind.Forecasting &&
      cumulativeData &&
      cumulativeData.lastProductionDate
    ) {
      const lastProdDate = new Date(Date.parse(cumulativeData.lastProductionDate));
      const forecastDate = new Date(Date.parse(segment.startDate));
      if (forecastDate < lastProdDate) {
        warnings.push({
          product: segment.product,
          segment: segmentNumber,
          message: `Segment ${segmentNumber}: Forecast start date is before production ends`
        });
      }
    }
  });
  return {
    errors,
    warnings
  };
};

export function updateHeaders(template: SegmentTemplate, declineType: string) {
  const updatedRowHeaders = template?.rowHeaders.map((header: string) => {
    if (header === "Di" || header === "D1f" || header === "Df" || header === "D2f") {
      return getDeclineTitleWithType(header, declineType);
    } else {
      return header;
    }
  });

  const updatedTemplate = {
    ...template
  };
  updatedTemplate.rowHeaders = updatedRowHeaders;

  return updatedTemplate;
}

export const getOverallTemplateName = ({
  productSegmentTemplate,
  overallSegmentTemplate
}: {
  productSegmentTemplate: ProductSegmentTemplate;
  overallSegmentTemplate?: SegmentTemplate;
}): string => {
  const filteredTemplates: SegmentTemplate[] = Object.values(
    productSegmentTemplate
  ).filter(
    (template) =>
      template.productCols.length > 0 &&
      !isRatio(template.productCols[0]) &&
      !isImportedRateTemplate(template.productCols[0], template)
  );
  if (filteredTemplates.length === 0) {
    return overallSegmentTemplate?.name;
  }
  const firstTemplate = filteredTemplates[0]?.name;
  const allSame = filteredTemplates.every((template) => template?.name === firstTemplate);
  return allSame ? firstTemplate : "Mixed Templates";
};

export const updateArps = ({
  template,
  productArps,
  header,
  val,
  typeWellSettings,
  declineType,
  cumulativeData,
  setCalculatedFields,
  calculatedFields,
  primaryProduct
}: {
  template: SegmentTemplate;
  productArps: ArpSegment[];
  header: string;
  val: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeWellSettings: any;
  declineType: string;
  cumulativeData: CumulativeData;
  setCalculatedFields: React.Dispatch<
    React.SetStateAction<{ [product: string]: string[] }>
  >;
  calculatedFields: { [product: string]: string[] };
  primaryProduct?: string;
}): ArpSegment[] => {
  if (productArps.length == 0 || !arpsWasm) {
    return [];
  }
  try {
    const defaultTypeWellSettings = typeWellSettings.settings?.find(
      (s: { product: string }) => s.product === productArps[0].product
    );

    const product = productArps[0]?.product;
    const isProductPrimary = product === primaryProduct;

    const beforeUpdate = getTypeWellTemplateDisplayFieldsAndData(
      template.name,
      productArps,
      declineType,
      defaultTypeWellSettings,
      isProductPrimary
    );

    const updatedArps = template.update({
      segmentTemplateName: template.name,
      productArps,
      declineType,
      header,
      val,
      cumulativeData
    });
    if (updatedArps && updatedArps[0].di === null) {
      return updatedArps;
    }

    const afterUpdate = getTypeWellTemplateDisplayFieldsAndData(
      template.name,
      updatedArps,
      declineType,
      defaultTypeWellSettings,
      isProductPrimary
    );

    const newCalculatedFields = getNewCalculatedFields(beforeUpdate, afterUpdate, header);

    if (header !== ArpFields.EUR && header !== ArpFields.TotalEUR) {
      setCalculatedFields(() => {
        newCalculatedFields.push(ArpFields.EUR, ArpFields.TotalEUR);
        return { ...calculatedFields, [productArps[0].product]: newCalculatedFields };
      });
      return updatedArps;
    }

    setCalculatedFields(() => {
      return { ...calculatedFields, [productArps[0].product]: newCalculatedFields };
    });

    return updatedArps;
  } catch (err) {
    if (err instanceof RangeError && err.message.includes("Invalid time value")) {
      throw {
        [productArps[0]
          .product]: `Segment ${template.numberOfSegments}: End date is unreachable`
      };
    }
    throw { [productArps[0].product]: err?.toString() };
  }
};

export const extendArpsWrapper = ({
  segmentTemplates,
  arps,
  templateName,
  wellKey
}: {
  segmentTemplates: SegmentTemplate[];
  arps: ArpSegment[];
  templateName: string;
  wellKey: string;
}): ArpSegment[] => {
  const templateSegment = segmentTemplates.find((item) => item.name === templateName);
  if (arps.length === 0) {
    return arps;
  }
  return templateSegment.extendArps({ productArps: arps, wellKey });
};

const PRODUCT_NAMES = [
  ProductNames.Oil,
  ProductNames.Gas,
  ProductNames.Water,
  ProductNames.Condensate,
  ProductNames.Steam
] as const;

export const getDefaultTypeWellTemplate = (params: {
  kind: ArpsDesignerKind;
  typeWellTemplates: SegmentTemplate[] | undefined;
}): SegmentTemplate | undefined => {
  if (params.kind === ArpsDesignerKind.Forecasting) {
    return params.typeWellTemplates[1];
  }
  return params.typeWellTemplates[1]; // Default to Two Seg. Hyperbolic
};

// TODO: Refactor this function. It's doing too much. And getData is not a good name for it.
export const getData = ({
  arps,
  arpsLib,
  segmentTemplateName,
  kind,
  setTemplateError,
  typeWellTemplates,
  cumulativeData,
  declineType,
  typeWellSettings,
  updateEUR,
  primaryProduct,
  primaryProductEndDate
}: {
  arps: ICheckedForecast;
  arpsLib: typeof import("wasm/arps");
  segmentTemplateName: string;
  kind: ArpsDesignerKind;
  setTemplateError: React.Dispatch<React.SetStateAction<string | undefined>>;
  typeWellTemplates: SegmentTemplate[];
  cumulativeData: CumulativeData;
  declineType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeWellSettings: any;
  updateEUR: (params: UpdateEURParams) => {
    eur: string;
    remaining: string;
  };
  primaryProduct?: string;
  primaryProductEndDate?: Date | null;
}): SegmentTemplate => {
  setTemplateError(undefined);
  if (!arps || !arps.arps || !arps.arps.length) {
    if (segmentTemplateName) {
      const selectedTemplate = typeWellTemplates.find(
        (t) => t.name === segmentTemplateName
      );
      if (selectedTemplate) {
        return selectedTemplate;
      }
    }
    return getDefaultTypeWellTemplate({ kind, typeWellTemplates });
  }

  const primaryProductSegments = [...arps.arps].sort((item1, item2) =>
    isPrimaryProduct(item1.product) === isPrimaryProduct(item2.product)
      ? 0
      : isPrimaryProduct(item1.product)
      ? -1
      : 1
  );

  const productGroup: { [product: string]: ArpSegment[] } = {};
  if (!primaryProductSegments) {
    return typeWellTemplates[1];
  }
  for (const segment of primaryProductSegments) {
    if (!(segment.product in productGroup)) {
      productGroup[segment.product] = [];
    }
    productGroup[segment.product].push(segment);
  }

  const maxSegments = Object.values(productGroup).reduce((longest, current) => {
    return current.length > longest.length ? current : longest;
  }, [] as ArpSegment[]);

  // Use the comprehensive template detection which handles SAGD and other special cases
  let foundSegment = getArpsSegmentTemplate({
    segments: maxSegments,
    typeWellTemplates
  });

  // If user explicitly selected a template name, try to honor that selection
  if (segmentTemplateName && !foundSegment) {
    foundSegment = typeWellTemplates.find((t) => t.name === segmentTemplateName);
  }

  // Fallback to simplified matching if still not found
  if (!foundSegment) {
    foundSegment = typeWellTemplates.find(
      (item) =>
        item.numberOfSegments === maxSegments.length &&
        item.name.includes("Ramp-Up") ===
          isRampUpSegment({ segmentTemplateName, segments: maxSegments })
    );
  }

  if (!foundSegment) {
    if (maxSegments.length > 0) {
      // Warn the user that the number of segments is not supported
      setTemplateError("Type well format is not editable.");
    }

    // If no segment template is found, use the second one
    return typeWellTemplates[1];
  }
  const clonedDataTemplates = Object.assign({}, foundSegment);
  clonedDataTemplates.productDisplayData = [];

  clonedDataTemplates.productCols = Object.keys(productGroup);

  for (const product of clonedDataTemplates.productCols) {
    const productSegments = productGroup[product] as ArpSegment[];
    if (!productSegments || !arpsLib) {
      continue;
    }

    if (productSegments.length > 0) {
      const defaultTypeWellSettings = typeWellSettings.settings?.find(
        (s) =>
          s.product === productSegments[0].product ||
          (s.product == ProductNames.Condensate &&
            product == ProductNames.CondAbbreviatedWithDot)
      );

      const isProductPrimary = product === primaryProduct;

      const displayData = getTypeWellTemplateDisplayFieldsAndData(
        segmentTemplateName,
        productSegments,
        declineType,
        defaultTypeWellSettings,
        isProductPrimary,
        primaryProductEndDate
      );

      clonedDataTemplates.productDisplayData.push(displayData);

      const lastProductDataIndex = clonedDataTemplates.productDisplayData.length - 1;

      // Add the cumToDate production data for primary products
      if (kind === ArpsDesignerKind.Forecasting && lastProductDataIndex >= 0) {
        setCumulativeToDate(
          product,
          clonedDataTemplates,
          lastProductDataIndex,
          cumulativeData
        );
      }
    }
  }

  // Add the historical cumulative production to the predicted EUR from the forecast,
  // and add the newly added cumToDate to the input table
  if (kind === ArpsDesignerKind.Forecasting) {
    applyForecastingEurAndHeaders(clonedDataTemplates, {
      arps,
      cumulativeData,
      updateEUR
    });
  }
  return clonedDataTemplates;
};

// Shared with the read-only Imported path, which bypasses getData.
const applyForecastingEurAndHeaders = (
  clonedDataTemplates: SegmentTemplate,
  {
    arps,
    cumulativeData,
    updateEUR
  }: {
    arps: ICheckedForecast;
    cumulativeData: CumulativeData;
    updateEUR: (params: UpdateEURParams) => { eur: string; remaining: string };
  }
): void => {
  PRODUCT_NAMES.forEach((product) => {
    const index = clonedDataTemplates.productCols.indexOf(product);
    if (index >= 0) {
      const productData = clonedDataTemplates.productDisplayData[index] as ProductData;
      const { eur, remaining } = updateEUR({
        productName: product,
        cumulativeData,
        arps,
        forecastEur: parseFloat(productData?.EUR)
      });
      productData.TotalEUR = eur;
      productData.EUR = remaining;
    }
  });
  const eurIndex = clonedDataTemplates.rowHeaders.indexOf(ArpFields.EUR);
  if (eurIndex !== -1) {
    clonedDataTemplates.displayHeaders[eurIndex] = "Remaining";
  }

  if (eurIndex !== -1 && !clonedDataTemplates.rowHeaders.includes(ArpFields.CTD)) {
    clonedDataTemplates.rowHeaders.splice(eurIndex, 0, ArpFields.CTD);
    clonedDataTemplates.displayHeaders.splice(eurIndex, 0, ArpFields.CTD);
  }

  if (!clonedDataTemplates.rowHeaders.includes(ArpFields.TotalEUR)) {
    clonedDataTemplates.rowHeaders.push(ArpFields.TotalEUR);
    clonedDataTemplates.displayHeaders.push(ArpFields.EUR);
  }
};

const setCumulativeToDate = (
  product: string,
  clonedDataTemplates: SegmentTemplate,
  lastIndex: number,
  cumulativeData?: CumulativeData
) => {
  const productMap = {
    [ProductNames.Oil]: cumulativeData?.oil,
    [ProductNames.Condensate]: cumulativeData?.oil,
    [ProductNames.Gas]: cumulativeData?.gas,
    [ProductNames.Water]: cumulativeData?.water,
    [ProductNames.CondAbbreviatedWithDot]: cumulativeData?.condensate,
    [ProductNames.Steam]: cumulativeData?.steam
  };

  if (product in productMap) {
    (clonedDataTemplates.productDisplayData[lastIndex] as ProductData).CTD =
      productMap[product] ?? 0;
  }
};

export const getSegmentTypeUsingArps = ({
  arps,
  typeWellTemplates,
  kind
}: {
  arps: ICheckedForecast;
  typeWellTemplates: SegmentTemplate[];
  kind?: ArpsDesignerKind;
}): ProductSegmentTemplate => {
  if (!arps?.arps) {
    return {};
  }

  const productGroup: { [product: string]: ArpSegment[] } = {};
  for (const segment of arps.arps) {
    if (isRatio(segment.product)) continue;
    if (!(segment.product in productGroup)) {
      productGroup[segment.product] = [];
    }
    productGroup[segment.product].push(segment);
  }

  const updatedProductTemplates: ProductSegmentTemplate = {};

  // Matches the cutoff rows real templates get in TypeWell mode (useTypeWellTemplates.ts).
  const rowHeaders = [
    "Template",
    "Start Date",
    "Qi",
    "Di",
    "Df",
    "Qf",
    "End Date",
    ArpFields.EUR
  ];
  const displayHeaders = [
    "Template",
    "Start Date",
    "Q.i",
    "D.i",
    "D.f",
    "Q.f",
    "End Date",
    "EUR"
  ];
  if (kind !== ArpsDesignerKind.Forecasting) {
    rowHeaders.push(ArpFields.RestrictedEUR, ArpFields.QfAtCutoff);
    displayHeaders.push("EUR.Cutoff", "Q.f.Cutoff");
  }

  for (const product in productGroup) {
    const segments = productGroup[product];
    const template = getArpsSegmentTemplate({
      segments,
      typeWellTemplates
    });

    // No accessible template matches -- show it read-only instead of dropping it.
    updatedProductTemplates[product] = template ?? {
      name: IMPORTED_TEMPLATE_NAME,
      type: null,
      numberOfSegments: segments.length,
      productCols: [product],
      productDisplayData: [],
      rowHeaders: [...rowHeaders],
      displayHeaders: [...displayHeaders],
      update: null,
      initialData: null,
      extendArps: null
    };
  }
  return updatedProductTemplates;
};

export function getAbbreviatedSegmentLabel(segmentName: string): string {
  let label: string;
  switch (true) {
    case segmentName === "One Seg.":
      label = "1 Seg.";
      break;
    case segmentName === "Two Seg. Hyperbolic":
      label = "2 Seg.";
      break;
    case segmentName === "Two Seg. Hyperbolic with Ramp-Up":
      label = `2 Seg. Ramp`;
      break;
    case segmentName === "Three Seg. Transient":
      label = `3 Seg.`;
      break;
    case segmentName === "Three Seg. Transient with Ramp-Up":
      label = `3 Seg. Ramp`;
      break;
    case segmentName === "Three Seg. Transient with Constrained Period":
      label = `3 Seg. Constr.`;
      break;
    case segmentName === "SAGD":
      label = `SAGD`;
      break;
    default:
      label = segmentName;
  }
  return label;
}

/*
  This function takes a ProductSegmentTemplate object and returns an object containing
  unique row headers and their corresponding display headers.

  1. The order of row headers is based on the longest template.
  2. Row headers from shorter templates that do not appear in the longest template are inserted at their correct position
     based on their original position in the shorter template.
  3. Ratio products are handled specially: their fields (Qo, Qi, Duration) are inserted around the "Qi" position
     (only in TypeWell mode - Forecasting mode shows just Qi for ratios)
 */
export function getUniqueRowAndDisplayHeadersSorted(
  productSegmentTemplate: ProductSegmentTemplate,
  kind?: ArpsDesignerKind
): { rowHeaders: string[]; displayHeaders: string[] } {
  if (!productSegmentTemplate || Object.keys(productSegmentTemplate).length === 0) {
    return {
      rowHeaders: [],
      displayHeaders: []
    };
  }

  const isAllRatioProducts = Object.keys(productSegmentTemplate).every(
    (product: string) => isDecliningRatio(product)
  );
  if (isAllRatioProducts) {
    // In Forecasting mode, only show Qi for ratios (no Q0/Duration)
    if (kind === ArpsDesignerKind.Forecasting) {
      return {
        rowHeaders: [ArpFields.Qi],
        displayHeaders: ["Q.i"]
      };
    }
    // In TypeWell mode, show Q0, Duration, and Qi for declining ratios
    return {
      rowHeaders: [ArpFields.Q0, ArpFields.Duration, ArpFields.Qi],
      displayHeaders: ["Q.0", "Duration", "Q.i"]
    };
  }

  const filteredSegmentTemplates: SegmentTemplate[] = Object.values(
    productSegmentTemplate
  ).filter(
    (template) => template?.productCols.length > 0 && !isRatio(template.productCols[0])
  );

  const longestTemplate = filteredSegmentTemplates.reduce(
    (prev, curr) => (prev.rowHeaders.length > curr.rowHeaders.length ? prev : curr),
    { rowHeaders: [], displayHeaders: [] }
  );

  const longestRowHeaders = longestTemplate.rowHeaders;
  const longestDisplayHeaders = longestTemplate.displayHeaders;

  const uniqueFieldsMap = new Map<string, string>();

  // Add fields from the longest template first, preserving order.
  longestRowHeaders.forEach((header, index) => {
    uniqueFieldsMap.set(header, longestDisplayHeaders[index]);
  });

  // Check if there are any declining ratio products - if so, insert ratio fields around "Qi"
  // (only in TypeWell mode)
  const hasRatioProducts =
    kind === ArpsDesignerKind.TypeWell &&
    Object.keys(productSegmentTemplate).some((product) => isDecliningRatio(product));

  if (hasRatioProducts) {
    // Check which ratio fields already exist in the template
    const hasQ0 = uniqueFieldsMap.has(ArpFields.Q0);
    const hasQi = uniqueFieldsMap.has(ArpFields.Qi);
    const hasDuration = uniqueFieldsMap.has(ArpFields.Duration);

    // Insert ratio fields around "Qi" position, but only if they don't already exist
    const entries = Array.from(uniqueFieldsMap.entries());
    const qiIndex = entries.findIndex(([header]) => header === ArpFields.Qi);
    if (qiIndex >= 0) {
      // Rebuild the map with ratio fields inserted at correct positions
      // Order: Q0, Duration, Qi
      uniqueFieldsMap.clear();

      entries.forEach(([header, displayHeader], index) => {
        // When we reach the Qi field, insert Q0 and Duration BEFORE it
        if (index === qiIndex) {
          if (!hasQ0) uniqueFieldsMap.set(ArpFields.Q0, "Q.0");
          if (!hasDuration) uniqueFieldsMap.set(ArpFields.Duration, ArpFields.Duration);
          uniqueFieldsMap.set(header, displayHeader); // Add Qi itself
        } else {
          uniqueFieldsMap.set(header, displayHeader);
        }
      });
    } else {
      // No "Qi" field found, append missing ratio fields at the end
      if (!hasQ0) uniqueFieldsMap.set(ArpFields.Q0, "Q.0");
      if (!hasDuration) uniqueFieldsMap.set(ArpFields.Duration, ArpFields.Duration);
      if (!hasQi) uniqueFieldsMap.set(ArpFields.Qi, "Q.i");
    }
  }

  // Process each template to insert extra fields that are missing from the longest template.
  // Inserts extra fields from shorter templates at their correct template index.
  Object.values(filteredSegmentTemplates).forEach((template) => {
    const templateRowHeaders = template.rowHeaders;
    const templateDisplayHeaders = template.displayHeaders;

    templateRowHeaders.forEach((header, index) => {
      if (!longestRowHeaders.includes(header) && !uniqueFieldsMap.has(header)) {
        const previousHeader = templateRowHeaders[index - 1];
        const previousHeaderIndexInLongest = longestRowHeaders.indexOf(previousHeader);
        let insertIndex: number;

        const isValidPreviousHeader = previousHeaderIndexInLongest !== -1;
        if (isValidPreviousHeader) {
          insertIndex = previousHeaderIndexInLongest + 1;
        } else {
          // Insert at the position corresponding with the shorter template's index.
          insertIndex = index;
        }

        const keys = Array.from(uniqueFieldsMap.keys());
        const values = Array.from(uniqueFieldsMap.values());

        keys.splice(insertIndex, 0, header);
        values.splice(insertIndex, 0, templateDisplayHeaders[index]);

        // Rebuild the map based on the updated keys and values.
        uniqueFieldsMap.clear();
        keys.forEach((key, idx) => {
          uniqueFieldsMap.set(key, values[idx]);
        });
      }
    });
  });

  return {
    rowHeaders: Array.from(uniqueFieldsMap.keys()),
    displayHeaders: Array.from(uniqueFieldsMap.values())
  };
}
export function updateProductTemplatesAndData(
  params: UpdateProductTemplatesParams
): ProductSegmentTemplate {
  const {
    arps,
    typeWellTemplates,
    arpsWasm,
    kind,
    setTemplateError,
    cumulativeData,
    declineType,
    typeWellSettings,
    updateEUR,
    wellKey,
    primaryProductEndDate
  } = params;

  const productSegmentTemplate: ProductSegmentTemplate = getSegmentTypeUsingArps({
    arps,
    typeWellTemplates,
    kind
  });

  // Call getData for each arps product and update the template
  const nonRatioProductSegmentTemplate: ProductSegmentTemplate = Object.fromEntries(
    Object.entries(productSegmentTemplate).filter(([product]) => !isRatio(product))
  );

  // Determine the primary product (first non-ratio product in the template)
  const primaryProductKey = Object.keys(nonRatioProductSegmentTemplate)[0];

  for (const product in nonRatioProductSegmentTemplate) {
    const filteredArps = {
      ...arps,
      arps: arps.arps.filter((segment) => segment.product === product)
    } as UserArpsItem;

    // Skip getData -- its segment-count fallback would mislabel this as an editable template.
    if (isImportedRateTemplate(product, productSegmentTemplate[product])) {
      const defaultTypeWellSettings = typeWellSettings.settings?.find(
        (s: { product: string }) => s.product === product
      );
      const importedTemplate: SegmentTemplate = {
        ...productSegmentTemplate[product],
        productDisplayData: [
          {
            ...getTypeWellTemplateDisplayFieldsAndData(
              IMPORTED_TEMPLATE_NAME,
              filteredArps.arps,
              declineType,
              defaultTypeWellSettings,
              product === primaryProductKey,
              primaryProductEndDate
            ),
            Template: IMPORTED_TEMPLATE_NAME
          }
        ]
      };
      if (kind === ArpsDesignerKind.Forecasting) {
        setCumulativeToDate(product, importedTemplate, 0, cumulativeData);
        applyForecastingEurAndHeaders(importedTemplate, {
          arps: filteredArps,
          cumulativeData,
          updateEUR
        });
      }
      productSegmentTemplate[product] = updateHeaders(importedTemplate, declineType);
      continue;
    }

    productSegmentTemplate[product] = getData({
      arps: filteredArps,
      arpsLib: arpsWasm,
      segmentTemplateName: productSegmentTemplate[product].name,
      kind,
      setTemplateError,
      typeWellTemplates,
      cumulativeData,
      declineType,
      typeWellSettings,
      updateEUR,
      primaryProduct: primaryProductKey,
      primaryProductEndDate
    });
    productSegmentTemplate[product] = updateHeaders(
      productSegmentTemplate[product],
      declineType
    );

    if (productSegmentTemplate[product].name !== "One Seg.") {
      const productData = filteredArps.arps[filteredArps.arps.length - 1] ?? {};
      const dfKey = Object.keys(productData).find((key) =>
        key.toLowerCase().startsWith("df")
      );
      if (dfKey) {
        updateSessionDf({
          key: wellKey,
          product,
          newDf: productData[dfKey]
        });
      }
    }
  }

  if (arps?.constants == undefined) {
    return productSegmentTemplate;
  }

  // Determine if we have any declining ratios - if so, non-declining ratios should align to Q0
  // (only in TypeWell mode - in Forecasting mode, all ratios are treated as simple constants)
  const hasDecliningRatios =
    kind === ArpsDesignerKind.TypeWell &&
    (arps.constants.some((c) => isDecliningRatio(c.product)) ||
      // Flagged user declines and imported ratio Arps (EVA-6256) both render as ratios.
      arps.arps?.some((seg) => isDecliningRatio(seg.product)));

  // Non-declining ratios align to Q0 if declining ratios exist, otherwise to Qi
  const nonDecliningRatioField = hasDecliningRatios ? ArpFields.Q0 : ArpFields.Qi;
  const nonDecliningRatioDisplayField = hasDecliningRatios ? "Q.0" : "Q.i";

  // Forecast constants - handle both constants and ratio decline segments
  for (const constant of arps.constants) {
    const product = constant.product;

    // declining ratios only available in TypeWell mode
    if (kind === ArpsDesignerKind.TypeWell && isDecliningRatio(product)) {
      const ratioValues = getRatioDisplayValues(arps, product);

      const displayData: { [key: string]: number | undefined } = {};
      displayData[ArpFields.Q0] = ratioValues.qo;
      displayData[ArpFields.Duration] = ratioValues.duration;
      displayData[ArpFields.Qi] = ratioValues.qi;

      productSegmentTemplate[product] = {
        ...productSegmentTemplate[product],
        rowHeaders: [ArpFields.Q0, ArpFields.Duration, ArpFields.Qi], // Order: Q0, Duration, Qi
        displayHeaders: ["Q.0", "Duration", "Q.i"],
        productDisplayData: [displayData],
        productCols: [product]
      };
    } else {
      // For non-declining ratios (Shrinkage, plant liquids, etc.)
      // Align to Q0 if declining ratios exist (for keyboard navigation), otherwise Qi
      productSegmentTemplate[product] = {
        ...productSegmentTemplate[product],
        rowHeaders: [nonDecliningRatioField],
        displayHeaders: [nonDecliningRatioDisplayField],
        productDisplayData: [{ [nonDecliningRatioField]: constant.value }],
        productCols: [product]
      };
    }
  }

  // Also handle declining ratio products that might be stored as segments only
  // (only in TypeWell mode)
  if (kind === ArpsDesignerKind.TypeWell) {
    // Include both user-created simple declines (isUserRatioDecline) and imported ratio
    // Arps (EVA-6256). The latter aren't flagged and may have any segment count; they're
    // shown read-only (see TypeWellInputTable) as a Q0/Duration/Qi summary.
    const ratioSegments = arps.arps?.filter((seg) => isDecliningRatio(seg.product)) || [];

    // Get unique ratio products from segments that aren't already in constants
    const uniqueRatioProducts = [
      ...new Set(ratioSegments.map((seg) => seg.product))
    ].filter((product) => !arps.constants.some((c) => c.product === product));

    for (const product of uniqueRatioProducts) {
      const ratioValues = getRatioDisplayValues(arps, product);

      // Imported ratio Arps have no isUserRatioDecline flag; they're read-only.
      const isImportedRatioArps = !arps.arps.some(
        (seg) => seg.product === product && seg.isUserRatioDecline === true
      );

      // Build display data using the correct field names to match template
      // Order: Template, Q0, Duration, Qi
      const displayData: { [key: string]: number | string | undefined } = {};
      displayData["Template"] = isImportedRatioArps ? IMPORTED_TEMPLATE_NAME : "2 Seg.";
      displayData[ArpFields.Q0] = ratioValues.qo;
      displayData[ArpFields.Duration] = ratioValues.duration;
      displayData[ArpFields.Qi] = ratioValues.qi;

      // Ratio decline segments use a fixed "2 Seg." template (linear decline + flat)
      // Template field is shown but disabled since structure cannot be changed
      productSegmentTemplate[product] = {
        rowHeaders: ["Template", ArpFields.Q0, ArpFields.Duration, ArpFields.Qi],
        displayHeaders: ["Template", "Q.0", "Duration", "Q.i"],
        productDisplayData: [displayData],
        productCols: [product],
        name: isImportedRatioArps ? IMPORTED_TEMPLATE_NAME : "2 Seg.",
        type: null,
        numberOfSegments: 2,
        update: updateRatioDeclineSegments, // Update function for ratio decline segments
        initialData: null,
        extendArps: null
      };
    }
  }

  // Empty map also counts as all-unmatched.
  if (
    arps.arps.length > 0 &&
    Object.entries(productSegmentTemplate).every(([p, t]) => isImportedRateTemplate(p, t))
  ) {
    // Warn the user that the number of segments is not supported.
    setTemplateError("Type well format is not editable.");
  }

  return productSegmentTemplate;
}

/**
 * Sorts a ProductSegmentTemplate object based on a predefined priority order, placing the primary product first;
 * Any remaining products not in the priority list are sorted alphabetically (case-insensitive).
 *  */
export function getSortedProducts({
  updatedProductTemplates,
  kind,
  cumulativeData
}: {
  updatedProductTemplates: ProductSegmentTemplate;
  kind: ArpsDesignerKind;
  cumulativeData: CumulativeData;
}): ProductSegmentTemplate {
  if (Object.keys(updatedProductTemplates).length === 0) {
    return updatedProductTemplates;
  }

  const products: string[] = Object.keys(updatedProductTemplates);
  const primaryProduct: string = getPrimaryProduct({
    kind,
    products,
    cumulativeData
  });

  const remainingProducts = products.filter((p) => p !== primaryProduct);

  const productPriority = [
    "oil",
    "gas",
    "sales gas",
    "condensate",
    "cond",
    "water",
    "gor",
    "ogr",
    "cgr",
    "wgr",
    "wor",
    "o+w",
    "shrinkage",
    "c5+cg",
    "c2",
    "c2gr",
    "c3",
    "c3gr",
    "c4",
    "c4gr",
    "c5",
    "c5gr",
    "c5+gr",
    "heatingvalue"
  ];

  const sortedRemaining = remainingProducts.sort((a, b) => {
    const indexA = productPriority.indexOf(a.toLowerCase());
    const indexB = productPriority.indexOf(b.toLowerCase());

    const inA = indexA !== -1;
    const inB = indexB !== -1;

    if (inA && inB) return indexA - indexB;
    if (inA) return -1;
    if (inB) return 1;
    return a.localeCompare(b); // Fallback to alphabetical asc.
  });

  const sortedTemplates: ProductSegmentTemplate =
    primaryProduct in updatedProductTemplates
      ? { [primaryProduct]: updatedProductTemplates[primaryProduct] }
      : {};

  for (const product of sortedRemaining) {
    sortedTemplates[product] = updatedProductTemplates[product];
  }

  return sortedTemplates;
}

export const getPrimaryProduct = (params: {
  products: string[];
  kind: ArpsDesignerKind;
  cumulativeData: CumulativeData;
}): string | undefined => {
  if (params.kind === ArpsDesignerKind.Forecasting && params.cumulativeData) {
    const primaryProduct = params.cumulativeData.primaryProduct;
    if (
      params.products.includes(ProductNames.Condensate) &&
      primaryProduct === ProductNames.Oil
    ) {
      return ProductNames.Condensate;
    }
    return primaryProduct;
  }
  // Fallback: assume primary product is the first product added.
  const filteredProducts = params.products.filter((product) => !isRatio(product));
  if (filteredProducts.length === 0) {
    return undefined;
  }
  return filteredProducts[0];
};

export const toggleOverallTemplateDropdown = () => {
  const templateSelect = document.querySelector(
    ".overall-template-select .ant-select-selector"
  );

  // Opening the settings, does not hide the dropdown, so we need to check for that.
  if (!templateSelect || document.querySelector(".settings-content")) {
    return;
  }

  const dropdown = document.querySelector(".ant-select-dropdown");
  const isTemplateDropdownOpen =
    dropdown && !dropdown.classList.contains("ant-select-dropdown-hidden");
  if (isTemplateDropdownOpen) {
    // Close the dropdown
    dropdown.dispatchEvent(
      new MouseEvent("mousedown", { bubbles: true, cancelable: true })
    );
    const input = templateSelect.querySelector("input");
    if (input) {
      (input as HTMLElement).blur();
    }
  } else {
    // Open the dropdown
    templateSelect.dispatchEvent(
      new MouseEvent("mousedown", { bubbles: true, cancelable: true })
    );
    const input = templateSelect.querySelector("input");
    if (input) {
      (input as HTMLElement).focus();
    }
  }
};

// Time inputs a template can expose on its leading segment, in chronological
// priority. tCons is checked before tRamp because a constrained template's
// display fields populate BOTH (tCons mirrors the initial-segment duration), and
// the constrained period is the one we want to shrink.
const FIRST_TIME_FIELD_PRIORITY: ArpFields[] = [
  ArpFields.TCons,
  ArpFields.TRamp,
  ArpFields.TFlat,
  ArpFields.TTrans
];

/**
 * Identifies the first chronological "time" input for a product and its current
 * (displayed) duration in months. Used when walking a forecast up to the end of
 * production (the `c` shortcut) so the time input can be shrunk by the number of
 * months the start date moves (EVA-6722).
 *
 * Rather than re-deriving which field a template exposes from segment shape, this
 * reads the canonical display fields from `getTypeWellTemplateDisplayFieldsAndData`
 * — the same source the UI uses — and returns the first present, non-zero time
 * field. That keeps the two in lockstep and avoids fragile segment-count guesses.
 *
 * @returns the header to update (`tCons` / `tRamp` / `tFlat` / `tTrans`) and its
 *          current displayed duration, or `null` when the template has no time input.
 */
export const getFirstTimeSegmentField = ({
  segmentTemplateName,
  segments,
  declineType
}: {
  segmentTemplateName: string;
  segments: ArpSegment[];
  declineType: string;
}): { header: ArpFields; currentMonths: number } | null => {
  if (!segments || segments.length === 0) {
    return null;
  }

  const displayFields = getTypeWellTemplateDisplayFieldsAndData(
    segmentTemplateName,
    segments,
    declineType
  );

  for (const header of FIRST_TIME_FIELD_PRIORITY) {
    const value = displayFields[header];
    if (typeof value === "number" && value > 0) {
      return { header, currentMonths: value };
    }
  }

  return null;
};

export const updateForecastStartDateToNextProductionMonth = ({
  arps,
  cumulativeData,
  productSegmentTemplate,
  declineType,
  onChange
}: {
  arps: ICheckedForecast;
  cumulativeData: CumulativeData;
  productSegmentTemplate: ProductSegmentTemplate;
  onChange: (item: ICheckedForecast, hasErrors?: boolean, hasWarnings?: boolean) => void;
  declineType: string;
}) => {
  const productionEndDate: Date = new Date(cumulativeData?.lastProductionDate);
  const nextProductionMonth: Date = getFirstDayOfNextMonthUTC(productionEndDate);
  for (const product in productSegmentTemplate) {
    if (isRatio(product)) {
      continue;
    }
    if (isImportedRateTemplate(product, productSegmentTemplate[product])) {
      continue;
    }

    const productArps: ArpSegment[] = arps.arps.filter(
      (segment) => segment.product === product
    );

    const forecastStartDate: Date = new Date(productArps[0].startDate);
    if (forecastStartDate >= nextProductionMonth) {
      continue;
    }

    // Capture the time input (if any) and the original start date BEFORE moving
    // the forecast, so we can shrink the time by however far the start moves.
    // NOTE: these are eager scalar snapshots taken before the segment objects are
    // mutated below — do NOT make them lazy / re-read them after the move.
    const originalStartDate: string = productArps[0].startDate;
    const timeField = getFirstTimeSegmentField({
      segmentTemplateName: productSegmentTemplate[product].name,
      segments: productArps,
      declineType
    });

    const daysInBetween =
      getDurationInDays(productArps[0].startDate, nextProductionMonth.toISOString()) - 1; // Minus 1 because it's not inclusive.

    const seg1: ArpSegment = productArps[0];
    const qiOrig = seg1.qi;
    seg1.qi = Number(
      arpsWasm.getRateAtDay(seg1.qi, seg1.di, seg1.b, daysInBetween).toFixed(2)
    );
    // Re-parameterize di to stay on the same Arps curve from the new start point.
    // For hyperbolic/harmonic: D(t) = Di * (qi_new/qi_orig)^b
    // For exponential (b=0): Math.pow(..., 0) = 1, so di is unchanged.
    if (qiOrig > 0) {
      seg1.di = seg1.di * Math.pow(seg1.qi / qiOrig, seg1.b);
    }

    updateSegmentsFromHeaderValues({
      header: "Start Date",
      segmentTemplateName: productSegmentTemplate[product].name,
      segments: productArps,
      declineType,
      val: nextProductionMonth,
      cumulativeData
    });

    // For templates with a time input (SAGD, ramp-up, constrained, transient),
    // shrink that first time segment by the number of months the start moved
    // (EVA-6722). Routing through the template's own update reuses the zero ->
    // template-restructure logic (e.g. removing the ramp when the time hits 0).
    if (timeField) {
      // The forecast advances by whole production months (the start always lands
      // on the first of a month), so round the move to a whole number — otherwise
      // average-month arithmetic (~30.44 days) leaks decimals into the time input
      // (e.g. an 8mo input advanced 2 months would become 6.1 instead of 6).
      const monthsMoved = Math.round(
        avgMonthsDiff(originalStartDate, nextProductionMonth.toISOString())
      );
      const newTime = Math.max(0, roundTo(timeField.currentMonths - monthsMoved, 1));

      // ACCEPTED EDGE CASE: when newTime clamps to 0 on a *declining* constrained
      // segment (qi !== qf), the template update re-fits the decline over a
      // zero-day span (getDiAtDayFromQiToQf divides by ~0), producing a
      // non-finite di and a NaN EUR. This is intentional — it surfaces to the
      // forecaster as a signal to fix the template, rather than silently
      // fabricating a number. A flat constrained segment (qi === qf) collapses
      // cleanly. Verified by the "declining constrained ... NaN" test.
      const updatedSegments = productSegmentTemplate[product].update({
        segmentTemplateName: productSegmentTemplate[product].name,
        productArps,
        declineType,
        header: timeField.header,
        val: newTime,
        cumulativeData
      });

      // After the splice, `productArps` references the now-removed segment
      // objects — it is stale and must not be read again below.
      const startIndex = arps.arps.findIndex((segment) => segment.product === product);
      if (startIndex !== -1 && updatedSegments) {
        arps.arps.splice(startIndex, productArps.length, ...updatedSegments);
      }
    }
  }
  onChange && onChange(arps);
};

/**
 * Checks if a forecast series has all zero production values (rate, cum, and vol)
 *
 * @param rate_cum - Array of rate/cumulative data points to check
 * @returns true if all rate, cum, and vol values are zero across the entire rate_cum array,
 *          or if rate_cum is empty, null, or not an array; false if any non-zero value exists
 */
export function isTypeWellSeriesAllZeros(rate_cum: RateDto[]): boolean {
  if (!Array.isArray(rate_cum) || rate_cum.length === 0) {
    return true;
  }

  return !rate_cum.some(
    (point) => point.rate !== 0 || point.cum !== 0 || point.vol !== 0
  );
}

/**
 * Combines consecutive "Qf already reached" warnings into a single message with segment ranges.
 * This provides a cleaner UX in Product mode where multiple consecutive segments may be constrained.
 *
 * Example transformation:
 * Before:
 *   - "Segment 2: Not required, Qf already reached in segment 1"
 *   - "Segment 3: Not required, Qf already reached in segment 1"
 * After:
 *   - "Segments 2-3: Not required, Qf already reached in segment 1"
 *
 * @param warnings - Array of warning objects to process
 * @returns New array with combined consecutive "Qf already reached" warnings
 */
export function combineConsecutiveQfReachedWarnings(
  warnings: Array<{ product: string | number; segment?: number; message: string }>
): Array<{ product: string | number; segment?: number; message: string }> {
  if (!warnings || warnings.length === 0) {
    return warnings;
  }

  const warningsByProduct: {
    [product: string]: Array<{
      product: string | number;
      segment?: number;
      message: string;
    }>;
  } = {};

  warnings.forEach((warning) => {
    const productKey = `${warning.product}`;
    if (!warningsByProduct[productKey]) {
      warningsByProduct[productKey] = [];
    }
    warningsByProduct[productKey].push(warning);
  });

  const combinedWarnings: Array<{
    product: string | number;
    segment?: number;
    message: string;
  }> = [];

  Object.keys(warningsByProduct).forEach((productKey) => {
    const productWarnings = warningsByProduct[productKey].sort(
      (a, b) => (a.segment || 0) - (b.segment || 0)
    );

    let i = 0;
    while (i < productWarnings.length) {
      const currentWarning = productWarnings[i];

      const isQfReachedWarning = currentWarning.message.includes(
        "Not required, Qf already reached in segment"
      );

      if (!isQfReachedWarning || !currentWarning.segment) {
        // Not a Qf warning or no segment number, keep as-is
        combinedWarnings.push(currentWarning);
        i++;
        continue;
      }

      // Extract which segment reached Qf from the message
      const reachedSegmentMatch = currentWarning.message.match(/segment (\d+)$/);
      const reachedInSegment = reachedSegmentMatch ? reachedSegmentMatch[1] : "previous";

      // Find consecutive segments with the same "Qf already reached" message
      const consecutiveSegments: number[] = [currentWarning.segment];
      let j = i + 1;

      while (j < productWarnings.length) {
        const nextWarning = productWarnings[j];
        const isNextQfWarning = nextWarning.message.includes(
          "Not required, Qf already reached in segment"
        );
        const nextReachedMatch = nextWarning.message.match(/segment (\d+)$/);
        const nextReachedSegment = nextReachedMatch ? nextReachedMatch[1] : "previous";

        // Check if consecutive and same "reached in" segment
        if (
          isNextQfWarning &&
          nextWarning.segment &&
          nextWarning.segment ===
            consecutiveSegments[consecutiveSegments.length - 1] + 1 &&
          nextReachedSegment === reachedInSegment
        ) {
          consecutiveSegments.push(nextWarning.segment);
          j++;
        } else {
          break;
        }
      }

      // Create combined or single warning
      if (consecutiveSegments.length > 1) {
        const firstSegment = consecutiveSegments[0];
        const lastSegment = consecutiveSegments[consecutiveSegments.length - 1];
        combinedWarnings.push({
          product: currentWarning.product,
          segment: firstSegment,
          message: `Segments ${firstSegment}-${lastSegment}: Not required, Qf already reached in segment ${reachedInSegment}`
        });
      } else {
        combinedWarnings.push(currentWarning);
      }

      i = j;
    }
  });

  return combinedWarnings;
}
